<!-- Enhanced Monster Screen Component -->
<script setup lang="ts">
import { computed, inject, ref, onMounted, onBeforeUnmount, nextTick } from 'vue';
import monsterUrl from '/assets/monster/glad.png';
import SpeechBubble from '@/components/SpeechBubble.vue';

type ColorScheme = 'purple' | 'yellow' | 'green' | 'blue';

const colorScheme = inject<ColorScheme>('colorScheme', 'yellow');
const goToNextPage = inject<() => void>('goToNextPage');
const previousSlide = inject<() => void>('previousSlide');

if (!goToNextPage || !previousSlide) {
  console.error('Navigation functions not properly injected.');
}

const backgroundColor = computed(() => {
  const map = {
    purple: 'bg-purple-background',
    yellow: 'bg-yellow-background',
    green: 'bg-green-background',
    blue: 'bg-blue-background',
  };
  return map[colorScheme] ?? 'bg-yellow-background';
});

const isVisible = ref(false);
const isDesktop = ref(false); // Start with false
const navigationArea = ref<HTMLElement | null>(null);
const scrollButtonVisible = ref(false);
const isNavigating = ref(false);
const pageLoaded = ref(false);

// Create a single abort controller for all operations
let abortController: AbortController | null = null;

// Debounce function with proper typing
function debounce<T extends (...args: any[]) => any>(func: T, wait: number): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null;
  return function executedFunction(...args: Parameters<T>) {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => {
      func(...args);
      timeout = null;
    }, wait);
  };
}

// Improved device detection
const updateDeviceType = () => {
  try {
    isDesktop.value = window.innerWidth >= 768;
  } catch (error) {
    console.error('Error detecting device type:', error);
    isDesktop.value = false;
  }
};

const checkScrollVisibility = () => {
  if (!navigationArea.value || !pageLoaded.value) return;
  try {
    const rect = navigationArea.value.getBoundingClientRect();
    scrollButtonVisible.value = rect.top > window.innerHeight - 20;
  } catch (error) {
    console.error('Error checking scroll visibility:', error);
    scrollButtonVisible.value = false;
  }
};

// Debounced resize handler
const debouncedResize = debounce(() => {
  updateDeviceType();
  checkScrollVisibility();
}, 300);

onMounted(async () => {
  abortController = new AbortController();
  
  // Wait for next tick to ensure DOM is ready
  await nextTick();
  
  // Initialize in a more controlled manner
  try {
    updateDeviceType();
    pageLoaded.value = true;
    
    // Use double RAF for smoother initialization
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        isVisible.value = true;
        checkScrollVisibility();
      });
    });
    
    // Add event listeners with abort signal
    window.addEventListener('resize', debouncedResize, { 
      passive: true, 
      signal: abortController.signal 
    });
    
    window.addEventListener('orientationchange', debouncedResize, { 
      passive: true, 
      signal: abortController.signal 
    });
  } catch (error) {
    console.error('Error during mount:', error);
  }
});

onBeforeUnmount(() => {
  // Abort all operations
  if (abortController) {
    abortController.abort();
    abortController = null;
  }
  
  // Clean up any running timers
  isNavigating.value = false;
  pageLoaded.value = false;
});

function scrollToNavigationArea() {
  if (!navigationArea.value || isNavigating.value) return;
  
  try {
    // Use a safer scrolling method
    const element = navigationArea.value;
    const y = element.getBoundingClientRect().top + window.pageYOffset;
    window.scrollTo({ top: y, behavior: 'smooth' });
  } catch (error) {
    console.error('Error scrolling:', error);
  }
}

// Enhanced navigation handler
async function handleNavigationClick(fn: (() => void) | undefined, event: Event) {
  if (!fn || isNavigating.value || !pageLoaded.value) return;
  
  event.preventDefault();
  event.stopPropagation();
  event.stopImmediatePropagation(); // Prevent any bubbling
  
  const target = event.currentTarget as HTMLElement;
  if (!target) return;
  
  // Lock navigation
  isNavigating.value = true;
  
  try {
    // Disable all buttons immediately
    const buttons = document.querySelectorAll('.next-button, .back-button');
    buttons.forEach(btn => btn.setAttribute('disabled', 'true'));
    
    // Add visual feedback
    target.classList.add('button-active');
    
    // Use RAF to ensure rendering before navigation
    await new Promise(resolve => requestAnimationFrame(resolve));
    
    // Add page transition class
    document.body.classList.add('page-transitioning');
    
    // Small delay for visual feedback
    await new Promise(resolve => setTimeout(resolve, 100));
    
    // Execute navigation with error boundary
    try {
      fn();
    } catch (navError) {
      console.error('Navigation function error:', navError);
      throw navError;
    }
    
    // Wait for navigation to process
    await new Promise(resolve => setTimeout(resolve, 200));
    
  } catch (error) {
    console.error('Navigation error:', error);
    // Ensure cleanup happens even on error
    document.body.classList.remove('page-transitioning');
  } finally {
    // Always cleanup
    if (target && target.classList) {
      target.classList.remove('button-active');
    }
    
    // Re-enable buttons
    const buttons = document.querySelectorAll('.next-button, .back-button');
    buttons.forEach(btn => btn.removeAttribute('disabled'));
    
    // Unlock navigation after a safe delay
    setTimeout(() => {
      isNavigating.value = false;
      document.body.classList.remove('page-transitioning');
    }, 500);
  }
}

// Simplified click handlers
const onNextClick = (event: Event) => handleNavigationClick(goToNextPage, event);
const onBackClick = (event: Event) => handleNavigationClick(previousSlide, event);
</script>

<template>
  <div
    :class="[backgroundColor, { 'content-loaded': isVisible }]"
    class="monster-screen w-full h-full grid md:grid-cols-2 items-center overflow-y-auto"
  >
    <!-- Monster and Speech Bubble Section -->
    <div
      class="flex justify-center flex-col-reverse md:flex-row items-center relative md:w-[70%] mx-auto"
    >
      <img 
        class="monster -mt-14 md:mt-0 shrink-0" 
        :src="monsterUrl"
        loading="lazy"
        alt="Glad monster"
        decoding="async"
      />

      <div class="relative flex justify-center items-center">
        <speech-bubble
          :color="colorScheme"
          class="w-[100%] md:rotate-[70deg] -scale-x-100 -z-5"
        />

        <div
          class="absolute md:pl-5 lg:text-[15px] text-[min(5vw,1.15rem)] text-center w-[80%] left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
        >
          Bra! Nu tar vi några enkla fystester och frågor om din livsstil.
        </div>
      </div>
    </div>
    
    <!-- Navigation Buttons Section -->
    <div
      class="navigation-container md:!relative md:!bg-transparent"
      ref="navigationArea"
    >
      <!-- Scroll Button -->
      <div class="scroll-button-container" v-if="scrollButtonVisible && !isNavigating">
        <b-icon-button @click="scrollToNavigationArea" class="scroll-button">
          <arrow-down />
        </b-icon-button>
      </div>

      <!-- Navigation Buttons -->
      <div class="button-container">
        <b-button
          @click.passive="onBackClick"
          look="secondary"
          class="back-button"
          role="button"
          aria-label="Tillbaka"
          :disabled="isNavigating"
          :class="{ 'pointer-events-none': isNavigating }"
        >
          Tillbaka
        </b-button>

        <b-button
          @click.passive="onNextClick"
          class="next-button"
          role="button"
          aria-label="Nästa"
          :disabled="isNavigating"
          :class="{ 'pointer-events-none': isNavigating }"
        >
          Nästa
        </b-button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.monster-screen {
  position: relative;
  opacity: 0;
  transition: opacity 0.3s ease;
  /* Improve iOS performance */
  -webkit-transform: translateZ(0);
  transform: translateZ(0);
  -webkit-backface-visibility: hidden;
  backface-visibility: hidden;
  /* Prevent content shift */
  contain: layout style;
}

.content-loaded {
  opacity: 1;
}

/* Monster Image Styling - width handled by global img.monster in variables.css */
.monster {
  position: relative;
  contain: layout;
  will-change: auto;
}

/* Navigation Area Styling */
.navigation-container {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: #ffdd7d;
  padding: 1.5rem;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  z-index: 100;
  /* Improve performance */
  contain: layout style;
  transform: translateZ(0);
}

.scroll-button-container {
  position: absolute;
  top: -3rem;
  right: 1rem;
  z-index: 20;
}

.scroll-button {
  animation: pulse 2s infinite;
  will-change: transform;
}

.button-container {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  width: 100%;
  z-index: 10;
}

/* Button Styling - Enhanced iOS Support */
.next-button,
.back-button {
  min-height: 44px;
  min-width: 100px;
  position: relative;
  z-index: 20;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
  cursor: pointer;
  -webkit-appearance: none;
  appearance: none;
  -webkit-touch-callout: none;
  user-select: none;
  /* Performance optimizations */
  will-change: transform, opacity;
  transform: translateZ(0);
  -webkit-backface-visibility: hidden;
  backface-visibility: hidden;
  /* Prevent text selection on double tap */
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  /* Ensure proper sizing */
  flex-shrink: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  /* Improve click reliability */
  isolation: isolate;
}

/* Disabled state */
.next-button:disabled,
.back-button:disabled,
.next-button.pointer-events-none,
.back-button.pointer-events-none {
  opacity: 0.5;
  pointer-events: none !important;
  cursor: not-allowed;
}

/* Active state styling */
.button-active {
  opacity: 0.8;
  transform: scale(0.98) translateZ(0);
}

/* iOS Safari specific fixes */
@supports (-webkit-touch-callout: none) {
  .next-button,
  .back-button {
    padding: 14px 24px;
    margin: 2px;
    font-size: 16px;
    line-height: 1.2;
    /* Prevent zoom on double tap */
    touch-action: manipulation;
  }

  .button-container {
    margin-bottom: 10px;
    padding-bottom: env(safe-area-inset-bottom, 0);
  }
  
  .navigation-container {
    padding-bottom: calc(1.5rem + env(safe-area-inset-bottom, 10px));
  }
}

/* Responsive Adjustments */
@media (min-width: 768px) {
  .button-container {
    grid-template-columns: 1fr 1fr;
    max-width: 400px;
    margin: 0 auto;
  }
  
  .navigation-container {
    background-color: transparent !important;
    position: fixed !important;
    bottom: 0;
    left: 0;
    right: 0;
  }
}

/* iPhone Mini specific adjustments */
@media screen and (max-width: 375px) and (max-height: 667px) {
  .next-button,
  .back-button {
    width: 100%;
    padding: 16px 20px;
    font-size: 16px;
    line-height: 1.2;
  }

  .button-container {
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }
  
  .navigation-container {
    padding: 1rem;
    padding-bottom: calc(1rem + env(safe-area-inset-bottom, 10px));
  }
}

/* Animations with performance considerations */
@keyframes pulse {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.9;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes fadeOut {
  0% {
    opacity: 1;
  }
  100% {
    opacity: 0.6;
  }
}

.page-transitioning {
  animation: fadeOut 0.2s ease-out forwards;
  pointer-events: none !important;
}

/* Accessibility - Reduced Motion */
@media (prefers-reduced-motion: reduce) {
  .scroll-button,
  .button-active,
  .page-transitioning {
    animation: none !important;
  }
  
  .page-transitioning {
    opacity: 0.7;
  }
  
  * {
    transition-duration: 0.01ms !important;
  }
}

/* High contrast mode support */
@media (prefers-contrast: high) {
  .next-button,
  .back-button {
    border: 2px solid currentColor;
  }
}
</style>